import {
  Component,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnDestroy,
  HostListener,
} from '@angular/core';
import * as echarts from 'echarts';
import { EventStatisticData } from '../../../shared/interfaces/ride-data/event-statistic.interface';
import { RidesService } from '../../../shared/services/rides.service';

type EChartsOption = echarts.EChartsOption;

@Component({
  selector: 'app-bar-stack-normalization-chart',
  templateUrl: './bar-stack-normalization-chart.component.html',
  styleUrls: ['./bar-stack-normalization-chart.component.scss'],
  imports: [],
})
export class BarStackNormalizationChartComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;

  private chartData: EventStatisticData | null = null;
  private myChart: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;

  // Legend data for custom legend component
  public legendItems = [
    { label: 'Phone Used', color: '#22C55E' },
    { label: 'Acceleration', color: '#3B82F6' },
    { label: 'Speed Limit', color: '#fac858' },
    { label: 'Hard Braking', color: '#ee6666' },
    { label: 'Hard Return', color: '#73c0de' },
  ];

  constructor(private readonly _ridesService: RidesService) {}

  @HostListener('window:resize')
  onResize() {
    if (this.myChart) {
      // Force chart to resize when window resizes
      setTimeout(() => {
        this.myChart?.resize();
      }, 100);
    }
  }

  ngOnInit(): void {
    this.getEventsStatistics();
  }

  ngAfterViewInit(): void {
    // Render chart after view is initialized with a small delay to ensure DOM is ready
    setTimeout(() => {
      if (this.chartData) {
        this.renderChart(this.chartData);
      }
    }, 100);

    // Set up ResizeObserver to watch for container size changes
    if (this.chartContainer && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.myChart) {
          setTimeout(() => {
            this.myChart?.resize();
          }, 100);
        }
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.myChart) {
      this.myChart.dispose();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getEventsStatistics() {
    this._ridesService.getEventsStatistics().subscribe({
      next: (response: EventStatisticData) => {
        this.chartData = response;
        if (this.chartContainer) {
          this.renderChart(response);
        }
      },
      error: (error) => {
        console.error(
          'Error in component, should have been handled by service:',
          error,
        );
      },
    });
  }

  private renderChart(response: EventStatisticData) {
    if (!this.chartContainer) {
      console.warn('Chart container not available yet');
      return;
    }

    let rawData: Array<any> = [];

    const eventTypesData = [
      { Text: 'Phone Used', Value: 1 },
      { Text: 'Acceleration', Value: 2 },
      { Text: 'Speed Limit', Value: 3 },
      { Text: 'Hard Braking', Value: 4 },
      { Text: 'Hard Return', Value: 5 },
    ];

    let allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    for (let event of eventTypesData) {
      let eventArray: Array<any> = [];
      for (let day of allDays) {
        let value = response.data
          .filter((a) => a.dateOfService == day && a.eventTypeId == event.Value)
          .map((a) => a.count)[0];
        if (value) {
          eventArray.push(value);
        } else {
          eventArray.push(0);
        }
      }
      rawData.push(eventArray);
    }

    //Events Types
    let chartDom = this.chartContainer.nativeElement;
    if (!chartDom) {
      console.error('Chart DOM element not found');
      return;
    }

    // Dispose existing chart if it exists
    if (this.myChart) {
      this.myChart.dispose();
    }

    // Initialize chart without explicit dimensions - let it auto-detect container size
    this.myChart = echarts.init(chartDom, null, {
      renderer: 'canvas',
    });

    let option: EChartsOption;

    const series: any = [
      'Phone Used',
      'Acceleration',
      'Speed Limit',
      'Hard Braking',
      'Hard Return',
    ].map((name, sid) => {
      return {
        name,
        type: 'bar',
        stack: 'total',
        barWidth: '60%',
        label: {
          show: true,
          formatter: (params: any) => (params.value > 0 ? params.value : ''),
        },
        data: rawData[sid],
      };
    });

    option = {
      tooltip: {
        trigger: 'axis',
        confine: true, // Keep tooltip within chart bounds
        className: 'echarts-tooltip',
        textStyle: {
          fontSize: 12,
          lineHeight: 16,
        },
        padding: [8, 12],
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        show: false,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      yAxis: {
        type: 'value',
      },
      xAxis: {
        type: 'category',
        data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      },
      series,
      color: ['#22C55E', '#3B82F6', '#fac858', '#ee6666', '#73c0de'],
    };

    this.myChart.setOption(option);

    // Trigger immediate resize to ensure chart fits container
    this.myChart.resize();
  }
}
